import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bomb, Zap, Loader2, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { mapDepositError } from '@/lib/depositErrors';

interface DraftCard {
  id: string;
  name: string;
  description: string | null;
  rarity: string;
  cost: number;
  emoji: string | null;
  effect_type: string;
  min_round: number | null;
  target_scope: string;
}

interface TeamLite {
  id: string;
  name: string;
}

interface NFLDraftSabotagePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  myTeamId: string | null;
  myUserId: string | null;
  teams: TeamLite[];
  currentRound: number;
  /** Called after a successful play so the parent can refetch active effects + SP. */
  onPlayed?: () => void;
}

// Effects that don't take a target — confirmed without the picker step.
const LEAGUE_WIDE_EFFECTS = new Set(['reverse_order', 'league_guess_who']);

const RARITY_ORDER: Array<'common' | 'rare' | 'legendary'> = ['common', 'rare', 'legendary'];
const RARITY_LABEL: Record<string, string> = { common: 'Common', rare: 'Rare', legendary: 'Legendary' };
const RARITY_DOT: Record<string, string> = {
  common: 'bg-blue-500',
  rare: 'bg-purple-500',
  legendary: 'bg-amber-500',
};

export default function NFLDraftSabotagePanel({
  open,
  onOpenChange,
  leagueId,
  myTeamId,
  myUserId,
  teams,
  currentRound,
  onPlayed,
}: NFLDraftSabotagePanelProps) {
  const { toast } = useToast();
  const [cards, setCards] = useState<DraftCard[]>([]);
  const [spBalance, setSpBalance] = useState<number>(0);
  const [legendaryUsed, setLegendaryUsed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCard, setSelectedCard] = useState<DraftCard | null>(null);
  const [targetTeamId, setTargetTeamId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [revealedQueue, setRevealedQueue] = useState<Array<{ id: string; full_name: string; position: string; headshot_url: string | null }> | null>(null);
  const [revealedFor, setRevealedFor] = useState<string | null>(null);

  // Fetch cards + balance + legendary status when panel opens.
  useEffect(() => {
    if (!open || !leagueId || !myUserId) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      const [cardsRes, walletRes, legendaryRes] = await Promise.all([
        (supabase as any)
          .from('nfl_sabotage_cards')
          .select('id, name, description, rarity, cost, emoji, effect_type, min_round, target_scope')
          .eq('card_type', 'draft')
          .eq('is_active', true)
          .order('cost', { ascending: true }),
        (supabase as any)
          .from('player_wallets')
          .select('sabotage_credits')
          .eq('user_id', myUserId)
          .eq('league_id', leagueId)
          .maybeSingle(),
        // Any reverse_order or league_guess_who event for this league means
        // the legendary slot is consumed (one per draft, even after expiry).
        (supabase as any)
          .from('nfl_draft_sabotage_events')
          .select('id')
          .eq('league_id', leagueId)
          .in('effect_type', ['reverse_order', 'league_guess_who'])
          .limit(1),
      ]);

      if (cancelled) return;
      setCards((cardsRes.data || []) as DraftCard[]);
      setSpBalance(Number(walletRes.data?.sabotage_credits || 0));
      setLegendaryUsed(((legendaryRes.data as any[]) || []).length > 0);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [open, leagueId, myUserId]);

  // Reset card-flow state whenever the panel closes.
  useEffect(() => {
    if (!open) {
      setSelectedCard(null);
      setTargetTeamId(null);
      setSubmitting(false);
    }
  }, [open]);

  const grouped = useMemo(() => {
    const groups: Record<string, DraftCard[]> = { common: [], rare: [], legendary: [] };
    for (const c of cards) (groups[c.rarity] || groups.common).push(c);
    return groups;
  }, [cards]);

  const opponents = useMemo(
    () => teams.filter((t) => t.id !== myTeamId),
    [teams, myTeamId],
  );

  const lockReason = (card: DraftCard): string | null => {
    if (spBalance < card.cost) return `Need ${card.cost} SP`;
    if (card.min_round != null && currentRound < card.min_round) return `Round ${card.min_round}+`;
    if (card.rarity === 'legendary' && legendaryUsed) return 'Legendary used';
    return null;
  };

  const handleCardClick = (card: DraftCard) => {
    if (lockReason(card)) return;
    setSelectedCard(card);
    setTargetTeamId(null);
  };

  const handlePlay = async () => {
    if (!selectedCard || !leagueId) return;
    const isLeagueWide = LEAGUE_WIDE_EFFECTS.has(selectedCard.effect_type);
    if (!isLeagueWide && !targetTeamId) {
      toast({ title: 'Pick a target first', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await (supabase as any).functions.invoke('nfl-draft-sabotage', {
        body: {
          action: 'play_card',
          league_id: leagueId,
          card_id: selectedCard.id,
          target_team_id: isLeagueWide ? undefined : targetTeamId,
        },
      });
      if (error || !data?.success) {
        // SAB-662: supabase-js wraps a non-2xx in FunctionsHttpError with the JSON body on
        // error.context — read it so an RG block (self-exclusion / cool-off) shows the real
        // reason instead of the generic "Edge Function returned a non-2xx status code".
        let serverMsg: string | undefined = data?.error;
        let code: string | undefined = data?.code;
        if (!serverMsg && error) {
          try {
            const resp = (error as { context?: Response })?.context;
            if (resp && typeof resp.json === 'function') {
              const body = await resp.json();
              serverMsg = body?.error;
              code = body?.code;
            }
          } catch { /* body empty / unparseable */ }
        }
        // RG blocks route through the shared mapper (title + reason); other play errors keep
        // the raw server reason under a neutral title.
        const rgMapped = code === 'RG_BLOCKED' || code === 'PURCHASE_LIMIT'
          ? mapDepositError({ code, message: serverMsg })
          : null;
        toast({
          title: rgMapped?.title ?? 'Play failed',
          description: rgMapped?.message ?? serverMsg ?? error?.message ?? 'Failed to play card',
          variant: 'destructive',
        });
        setSubmitting(false);
        return;
      }

      toast({
        title: `Played ${selectedCard.name}!`,
        description: data.new_sp_balance != null ? `${data.new_sp_balance} SP remaining` : undefined,
      });

      // Film Study returns the queue inline. Show it after the panel closes
      // so the reveal isn't fighting for stacking with this Dialog.
      //
      // SAB-936: an EMPTY array is a real answer and must still open the reveal. The old
      // `length > 0` guard meant a buyer who paid 10 SP to spy on someone with no queue saw
      // nothing at all -- no modal, no explanation, just a debit. Array.isArray separates
      // "this was a Film Study play that found nothing" from "this was not Film Study"
      // (the server sends null for every other card).
      if (Array.isArray(data.revealed_queue)) {
        const targetName = teams.find((t) => t.id === targetTeamId)?.name || 'opponent';
        setRevealedQueue(data.revealed_queue);
        setRevealedFor(targetName);
      }

      onPlayed?.();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const isLeagueWide = selectedCard ? LEAGUE_WIDE_EFFECTS.has(selectedCard.effect_type) : false;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-md p-0 max-h-[90vh] flex flex-col">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base">
              {selectedCard ? (
                <button
                  onClick={() => { setSelectedCard(null); setTargetTeamId(null); }}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Back to card list"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              ) : (
                <Bomb className="h-4 w-4 text-primary" />
              )}
              <span>{selectedCard ? selectedCard.name : 'Draft Sabotage'}</span>
              <span className="ml-auto flex items-center gap-1 text-xs font-bold text-primary">
                <Zap className="h-3.5 w-3.5" />
                {spBalance} SP
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              {selectedCard
                ? (isLeagueWide ? 'Affects all teams for one round.' : 'Pick the team you want to sabotage.')
                : 'Play a card to disrupt your opponents during the draft.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !selectedCard ? (
              <div className="space-y-4">
                {RARITY_ORDER.map((rarity) => {
                  const items = grouped[rarity];
                  if (!items || items.length === 0) return null;
                  return (
                    <div key={rarity}>
                      <p className="flex items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                        <span className={cn('w-2 h-2 rounded-full', RARITY_DOT[rarity])} />
                        {RARITY_LABEL[rarity]}
                      </p>
                      <div className="space-y-2">
                        {items.map((card) => {
                          const reason = lockReason(card);
                          const locked = reason !== null;
                          return (
                            <button
                              key={card.id}
                              onClick={() => handleCardClick(card)}
                              disabled={locked}
                              className={cn(
                                'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors',
                                locked
                                  ? 'border-border/40 bg-muted/20 opacity-60 cursor-not-allowed'
                                  : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5',
                              )}
                            >
                              <span className="text-2xl shrink-0">{card.emoji || '💣'}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm font-semibold text-foreground">{card.name}</span>
                                  <Badge className="bg-damage/10 text-damage border-damage/20 text-[8px] px-1 py-0">
                                    {card.cost} SP
                                  </Badge>
                                  {card.min_round != null && (
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 border-violet-500/50 text-violet-400 bg-violet-500/10">
                                      Round {card.min_round}+
                                    </Badge>
                                  )}
                                  {LEAGUE_WIDE_EFFECTS.has(card.effect_type) && (
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 border-amber-500/50 text-amber-400 bg-amber-500/10">
                                      LEAGUE
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                  {card.description}
                                </p>
                                {locked && (
                                  <p className="text-[10px] text-amber-400 mt-1 font-medium">{reason}</p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Target-picker step
              <div className="space-y-3">
                <div className="rounded-lg border border-border bg-muted/20 p-3">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl shrink-0">{selectedCard.emoji || '💣'}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{selectedCard.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{selectedCard.description}</p>
                    </div>
                  </div>
                </div>

                {!isLeagueWide && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Target</p>
                    <div className="space-y-1 max-h-[260px] overflow-y-auto">
                      {opponents.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => setTargetTeamId(t.id)}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-md border text-sm transition-colors',
                            targetTeamId === t.id
                              ? 'border-primary/40 bg-primary/10 text-primary'
                              : 'border-border bg-card text-foreground hover:bg-muted/30',
                          )}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedCard && (
            <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Cost: <span className="font-semibold text-primary">{selectedCard.cost} SP</span>
              </p>
              <Button
                onClick={handlePlay}
                disabled={submitting || (!isLeagueWide && !targetTeamId)}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bomb className="h-4 w-4 mr-2" />}
                {isLeagueWide ? 'Play card' : 'Play on target'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Film Study reveal — separate Dialog so it stays open after the
          panel closes, and so its z-index doesn't fight the panel. */}
      <Dialog
        open={revealedQueue !== null}
        onOpenChange={(o) => { if (!o) { setRevealedQueue(null); setRevealedFor(null); } }}
      >
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🎬</span>
              Film Study
            </DialogTitle>
            <DialogDescription>
              {revealedQueue && revealedQueue.length > 0
                ? `${revealedFor}'s auto-pick queue, in their own priority order:`
                : `${revealedFor}'s auto-pick queue:`}
            </DialogDescription>
          </DialogHeader>
          {revealedQueue?.length === 0 && (
            <p data-testid="film-study-empty" className="text-sm text-muted-foreground">
              They have queued nobody. With an empty queue their auto-pick falls through to
              best-available rather than a list they chose — so nothing is being saved for
              later.
            </p>
          )}
          <ol className="space-y-2">
            {(revealedQueue || []).map((p, i) => (
              <li key={p.id} className="flex items-center gap-3 p-2 bg-muted/20 rounded-md">
                <span className="text-primary font-bold w-5 text-right tabular-nums">{i + 1}.</span>
                <div className="h-8 w-8 rounded-full bg-muted/50 overflow-hidden shrink-0 border border-border/40">
                  {p.headshot_url ? (
                    <img src={p.headshot_url} alt={p.full_name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-[9px] text-muted-foreground font-bold">
                      {p.position}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{p.full_name}</p>
                  <p className="text-[10px] text-muted-foreground">{p.position}</p>
                </div>
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
